import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type { UpdatePatientLabRequestBody } from '@/types/scheduling-api';

/**
 * PUT /api/Patients/UpdatePatientLabRequest?Id={id}
 *
 * Status transition for a single lab order — Phlebotomist marks a
 * sample as collected, doctor marks a result as reviewed, etc. Mirrors
 * Flutter `updatePatientLabRequestStatus` (`scheduling_data_source_impl
 * .dart:1176-1193`).
 *
 * Wire shape:
 *   - `Id` ships as URL query-string param (Flutter `?Id=${request.Id}`)
 *   - Body: `{ status: "<LAB_ORDER_STATUS literal>" }`
 *   - 2xx empty body — typed as `void` (Flutter `Success("")`)
 *
 * Cache invalidation: on success, invalidate the `['lab-orders', ...]`
 * query family so the list refetches and the row's new status surfaces.
 *
 * Wire verified against the 2026-05-21 dev curl (Andrew — PR #295 §1
 * fixup). Request body `{"status":"Sample collected"}` matches
 * `UpdatePatientLabRequestBody` exactly. Response body is `true` (not
 * empty); the hook discards the response either way.
 *
 * SECURITY — role posture (PR #295 §3 ESCALATE → Andrew direction
 * 2026-05-21):
 *   The backend does NOT enforce a Phlebotomist gate on this
 *   endpoint — Andrew confirmed and accepted the posture: the
 *   linked upload endpoints (`UploadPatientDocument` /
 *   `AddUpdatePatientDocument`) are intentionally cross-role for
 *   the regular Documents UX, and gating only the status-transition
 *   call would create an inconsistent picture without preventing
 *   the actual write (a determined caller could still upload + link
 *   via the open endpoints).
 *
 *   Defence model in this PR:
 *     1. React `tenantRoleId === '19'` gate hides the tab pill +
 *        bodies for non-Phlebotomist users on the happy path.
 *     2. `VITE_LAB_LINKAGE_ENABLED` feature flag (default OFF)
 *        keeps the surface fully dark in prod until Andrew flips
 *        the env per environment.
 *     3. PHI-safety in the dialog: file picker (no free-form GUID
 *        input) + server-issued document id + 4-field confirm step
 *        before commit.
 *
 *   What this is NOT: a security boundary against a determined
 *   authenticated caller. Any logged-in user who knows the URL can
 *   POST directly. Accept this when reviewing the surface.
 */

interface UpdateLabOrderStatusVariables {
  id: string;
  status: string;
}

export function useUpdateLabOrderStatus(): UseMutationResult<
  void,
  Error,
  UpdateLabOrderStatusVariables
> {
  const qc = useQueryClient();
  return useMutation<void, Error, UpdateLabOrderStatusVariables>({
    mutationFn: ({ id, status }) => {
      // Id is URL-encoded; the value is a backend GUID so safe under
      // encodeURIComponent.
      const url = `/api/Patients/UpdatePatientLabRequest?Id=${encodeURIComponent(id)}`;
      const body: UpdatePatientLabRequestBody = { status };
      return put<void>(url, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lab-orders'] });
    },
  });
}
